import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Gift, Users, TrendingUp, Trophy, ArrowRight, Clock, ChevronRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardOverview = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'sponsor') {
      fetchSponsorData();
    } else {
      fetchHomeownerData();
    }
  }, [user, token]);

  const fetchHomeownerData = async () => {
    try {
      const response = await axios.get(`${API}/entries/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSponsorData = async () => {
    try {
      const response = await axios.get(`${API}/sponsors/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  const isSponsor = user?.role === 'sponsor';

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-neutral-600 mt-1">
          {isSponsor ? "Here's your lead generation overview" : "Here's what's happening with your entries"}
        </p>
      </div>

      {/* Stats Cards */}
      {isSponsor && stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+12% this week</span>
            </div>
            <p className="text-3xl font-bold text-neutral-900">{stats.total_leads}</p>
            <p className="text-neutral-500">Total Leads</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-neutral-900">{stats.leads_by_score?.hot || 0}</p>
            <p className="text-neutral-500">Hot Leads</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-neutral-900">{stats.conversion_rate}%</p>
            <p className="text-neutral-500">Conversion Rate</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-amber-600" />
              </div>
              {stats.badge && (
                <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full">
                  {stats.badge}
                </span>
              )}
            </div>
            <p className="text-3xl font-bold text-neutral-900">${stats.cost_per_lead.toFixed(2)}</p>
            <p className="text-neutral-500">Cost per Lead</p>
          </motion.div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center mb-4">
              <Gift className="w-6 h-6 text-[#FF3B30]" />
            </div>
            <p className="text-3xl font-bold text-neutral-900">{entries.length}</p>
            <p className="text-neutral-500">Active Entries</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
              <Trophy className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-neutral-900">0</p>
            <p className="text-neutral-500">Wins</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-neutral-900">0</p>
            <p className="text-neutral-500">Referrals</p>
          </motion.div>
        </div>
      )}

      {/* Recent Entries / Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
              {isSponsor ? 'Recent Leads' : 'Recent Entries'}
            </h2>
            <Link to={isSponsor ? '/dashboard/leads' : '/dashboard/entries'} className="text-[#FF3B30] text-sm font-medium hover:underline">
              View all
            </Link>
          </div>

          {entries.length > 0 ? (
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <div key={entry.entry_id || index} className="flex items-center gap-4 p-3 rounded-xl hover:bg-neutral-50 transition-colors">
                  <img
                    src={entry.giveaway?.image_url || 'https://images.unsplash.com/photo-1772567732969-c1506edf80a0'}
                    alt={entry.giveaway?.title}
                    className="w-14 h-14 rounded-xl object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-900 truncate">{entry.giveaway?.title}</p>
                    <div className="flex items-center gap-2 text-sm text-neutral-500">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    entry.giveaway?.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-neutral-100 text-neutral-600'
                  }`}>
                    {entry.giveaway?.status || 'Active'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Gift className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500 mb-4">No entries yet</p>
              <Link to="/giveaways">
                <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
                  Browse Giveaways
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Outfit' }}>Quick Actions</h2>
          
          <div className="space-y-3">
            <Link to="/giveaways" className="flex items-center justify-between p-4 bg-[#FF3B30]/5 rounded-xl hover:bg-[#FF3B30]/10 transition-colors group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FF3B30] flex items-center justify-center">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Browse Giveaways</p>
                  <p className="text-sm text-neutral-500">Find new opportunities</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-[#FF3B30] group-hover:translate-x-1 transition-all" />
            </Link>

            <Link to="/dashboard/profile" className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl hover:bg-neutral-100 transition-colors group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neutral-200 flex items-center justify-center">
                  <Users className="w-5 h-5 text-neutral-600" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Complete Profile</p>
                  <p className="text-sm text-neutral-500">Improve your chances</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-neutral-900 group-hover:translate-x-1 transition-all" />
            </Link>

            <Link to="/dashboard/referrals" className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl hover:bg-neutral-100 transition-colors group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neutral-200 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-neutral-600" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Share & Earn</p>
                  <p className="text-sm text-neutral-500">Refer friends for rewards</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-neutral-900 group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
